import { combineEpics, ofType } from "redux-observable"
import { from, concat, of, EMPTY } from "rxjs"
import {
    flatMap,
    catchError,
    switchMap,
    delay,
    filter,
    map,
    ignoreElements,
} from "rxjs/operators"
import { Epic } from "../../epics"

import * as actions from "./actions"
import {
    addInformationNotification,
    addErrorNotification,
} from "../notifications/actions"
import { RequestCancelledError } from "../../errors"
import { buttonCallbacks } from "../contextmenuactions/oldui"
import { ViewMenuResponse } from "../../apis/models/datalist"

const autoDisappearDelay = 2000
export const defaultExecutionMessage =
    "Something went wrong during the execution of the script behind this button."

export const updateShareSuccessMessageVisibilityEpic: Epic = (
    action$,
    _state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.UPDATE_SHARESUCCESSMESSAGE_VISIBILITY),
        filter((action: actions.UpdateVisibility) => action.visibility),
        delay(autoDisappearDelay),
        map((action: actions.UpdateVisibility) => ({
            ...action,
            visibility: false,
        })),
    )

export const updateTitleEpic: Epic = (
    action$,
    state$,
    { resourceHeader: { ipeAPI } },
) =>
    action$.pipe(
        ofType(actions.UPDATE_TITLE),
        flatMap((action: actions.UpdateTitle) => {
            if (!state$.value.resourceHeader.resource.title.ipe) {
                return EMPTY
            }

            return from(
                ipeAPI.saveIPE(
                    state$.value.resourceHeader.resource.title.ipe,
                    action.title,
                ),
            ).pipe(
                ignoreElements(),
                catchError((error: unknown) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(
                        `Something went wrong while trying to update the title: ${JSON.stringify(
                            error,
                        )}`,
                    )
                    return of(
                        addErrorNotification(
                            "Something went wrong while trying to update the title.",
                        ),
                    )
                }),
            )
        }),
    )

export const executeCallButtonEpic: Epic = (action$, _state$, _dependencies$) =>
    action$.pipe(
        ofType(actions.EXECUTE_CALL_BUTTON),
        flatMap(({ button }: actions.ExecuteCallButton) => {
            const {
                buttonData: { actionId, callData },
            } = button
            const buttonUuid = button.uuid
            const callback = buttonCallbacks[actionId]
            if (!!callback) {
                const promise = callback(callData)
                if (promise) {
                    const promise$ = from(promise).pipe(
                        flatMap((result) => {
                            return result
                                ? [addInformationNotification(result)]
                                : [] // only when result, if not, no action
                        }),
                        catchError((error) => {
                            if (error instanceof RequestCancelledError) {
                                return EMPTY
                            }
                            console.error(error)
                            return of(
                                addErrorNotification(
                                    error || defaultExecutionMessage,
                                ),
                            )
                        }),
                    )
                    return concat(
                        of(actions.updateButtonLoading(buttonUuid, true)),
                        concat(
                            promise$,
                            of(actions.updateButtonLoading(buttonUuid, false)),
                        ),
                    )
                } else {
                    return EMPTY
                }
            } else {
                console.error(
                    `No callback found when trying to execute the following button: ${JSON.stringify(
                        button,
                    )}`,
                )
                return of(
                    addErrorNotification(
                        `Callback for action ${actionId} was not found.`,
                    ),
                )
            }
        }),
    )

export const requestListViewsEpic: Epic = (
    action$,
    state$,
    { resourceHeader: { datalistAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_LIST_VIEWS),
        switchMap(() => {
            const { selectionId, viewType } =
                state$.value.resourceHeader.changeViewData
            return !!selectionId
                ? from(datalistAPI.getViewMenuItems(selectionId, viewType))
                : EMPTY
        }),
        flatMap(({ Values, SelectedValue }: ViewMenuResponse) =>
            concat(
                of(actions.receiveListViews(Values)),
                of(actions.updateSelectedListView(SelectedValue)),
            ),
        ),
    )

export default combineEpics(
    updateShareSuccessMessageVisibilityEpic,
    updateTitleEpic,
    executeCallButtonEpic,
    requestListViewsEpic,
)
